import {
  getMomentTz,
  MESApiService,
  type spParameter,
  TimeService,
} from "@dexteel/mesf-core";
import type {
  LayerWeightLimits,
  ScrapMixAnalysis,
  ScrapMixFilterOption,
  ScrapMixFilters,
  ScrapMixStatistic,
  ScrapMixSummary,
} from "./models";
import { EMPTY_SCRAP_MIX_SUMMARY } from "./models";

const moment = getMomentTz();

const toCsv = (values: string[]) =>
  values.length > 0 ? values.join(",") : null;

export const getScrapMixAnalysis = async (
  filters: ScrapMixFilters,
  layerWeightLimits: LayerWeightLimits,
  signal?: AbortSignal,
): Promise<ScrapMixAnalysis> => {
  const apiService = new MESApiService();
  const plantZone = TimeService.getInstance().getServerTimeZone();
  const startDate = moment
    .tz(filters.from.format("YYYY-MM-DD"), plantZone)
    .startOf("day")
    .toISOString();
  const endDate = moment
    .tz(filters.to.format("YYYY-MM-DD"), plantZone)
    .add(1, "day")
    .startOf("day")
    .toISOString();

  const parameters: spParameter[] = [
    { name: "StartDate", value: startDate },
    { name: "EndDate", value: endDate },
    {
      name: "ExcludeOutOfRangeLayers",
      value: filters.excludeOutOfRangeLayers,
    },
    { name: "MinLayerWeight", value: layerWeightLimits.min },
    { name: "MaxLayerWeight", value: layerWeightLimits.max },
    { name: "ShiftCodes", value: toCsv(filters.shifts) },
    { name: "Crews", value: toCsv(filters.crews) },
    { name: "GradeIds", value: toCsv(filters.gradeIds) },
    {
      name: "RecipeFamilyIds",
      value: toCsv(filters.recipeFamilyIds),
    },
    { name: "OperatorIds", value: toCsv(filters.operatorIds) },
    { name: "ChargeNumbers", value: toCsv(filters.chargeNumbers) },
    { name: "HeatNames", value: toCsv(filters.heatNames) },
  ];

  const response = await apiService.callV2(
    "[SCRAP].[GetScrapMixAnalysis]",
    parameters,
    signal,
  );

  if (!response.ok) {
    throw new Error(response.message || "Failed to load scrap mix analysis");
  }

  const tables = response.data?.tables ?? [];
  const summary = (tables[0]?.rows?.[0] ??
    EMPTY_SCRAP_MIX_SUMMARY) as ScrapMixSummary;
  const statistics = (tables[1]?.rows ?? []) as ScrapMixStatistic[];
  const filterOptions = (tables[2]?.rows ?? []) as ScrapMixFilterOption[];

  return { summary, statistics, filterOptions };
};
